/**
 * web-studio-sites D4, D7: the kit's stub, built to `/webapp/kit/v1-stub.js`.
 * A site may load it as a plain script before its own scripts, so `Telify.draw`
 * can be called before the kit (`/webapp/kit/v1.js`) has loaded; the calls wait
 * in a queue the kit takes over. It imports nothing and exports nothing.
 *
 * It also shows the header-row pieces (`account`, `shop-bag-button`) as the last
 * page in this tab drew them, the moment their placeholders are parsed, so moving
 * between a site's pages never blanks them while the kit loads (card 4 walk,
 * Gabriel 2026-11-01). The copy is a picture, hidden from assistive technology and
 * inert, until the kit draws the piece and removes it (runtime.tsx, Placement).
 * The kit writes the copy under the same key and attribute; this file keeps its
 * own copy of both, being a plain script that imports nothing.
 */
import type { TelifyStubGlobal } from './global';

(() => {
  const w = window as unknown as { Telify?: TelifyStubGlobal };
  const telify = (w.Telify ??= {});
  if (typeof telify.draw === 'function') return;
  const queue = (telify.queue ??= []);
  telify.draw = (piece, drawing) => {
    queue.push([piece, drawing]);
  };

  const KEY = 'telify:remembered:';
  const ATTRIBUTE = 'data-telify-remembered';
  // A copy from before a kit release names classes the page's stylesheets no longer have: it is not shown.
  const styled = (html: string): boolean => {
    const first = /class="([^"\s]+)/.exec(html)?.[1];
    if (!first) return true;
    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList;
      try {
        rules = sheet.cssRules;
      } catch {
        continue;
      }
      for (const rule of Array.from(rules)) if (rule.cssText.includes(`.${first}`)) return true;
    }
    return false;
  };
  const paint = (el: Element) => {
    if (el.childElementCount > 0) return;
    let html: string | null = null;
    try {
      html = sessionStorage.getItem(KEY + el.getAttribute('data-telify'));
    } catch {
      return;
    }
    if (!html || !styled(html)) return;
    const copy = document.createElement('div');
    copy.setAttribute('data-telify-frame', 'content');
    copy.setAttribute(ATTRIBUTE, '');
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    copy.innerHTML = html;
    el.append(copy);
  };
  const scan = (root: Element | Document) => {
    if (root instanceof Element && root.hasAttribute('data-telify')) paint(root);
    root.querySelectorAll('[data-telify]').forEach(paint);
  };
  const observer = new MutationObserver((records) => {
    for (const record of records) record.addedNodes.forEach((node) => node instanceof Element && scan(node));
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
  scan(document);
  document.addEventListener('DOMContentLoaded', () => observer.disconnect(), { once: true });
})();
